/**
 * The low tier's baked card.
 *
 * On the cheapest devices the ceremony does not light a mesh every frame: the
 * card's turn is rendered ONCE into a strip of textures — one every few degrees
 * of yaw — and the ceremony draws sprites out of it. The card is a rigid body
 * turning about one axis, so a strip is not an approximation of the motion, it
 * is the motion, sampled; what is lost is the per-frame lighting sweep and the
 * pitch's slight nod, neither of which survives a small screen anyway.
 *
 * It is the same mesh and the same shader that bake it, so a low-tier card is
 * the high-tier card — at fewer angles.
 */

import { Container, RenderTexture, Sprite, Texture, type Renderer } from 'pixi.js';

import type { CardId } from '@/game/types';
import { CardView, type CardSpec, type Plates } from './card-view';
import { REST_PITCH, type Finish } from './timeline';

export interface SpinStrip {
  /** One texture every `step` degrees of yaw, starting at 0. */
  readonly textures: readonly Texture[];
  readonly step: number;
  /** The px-per-unit the strip was baked at, so a sprite can be scaled from it. */
  readonly pxPerUnit: number;
  /** The face the strip shows (the back is baked into the turn). */
  readonly face: CardId;
}

/** How many degrees apart the baked angles are. 15° is 24 frames for a full turn. */
export const BAKE_STEP = 15;

/**
 * Bake one card's full turn. The strip covers 0..360 exclusive, so any yaw
 * maps into it with a modulo and no edge case at the wrap.
 */
export function bakeSpin(
  renderer: Renderer,
  plates: Plates,
  face: CardId,
  finish: Finish,
  rankIndex: number,
  pxPerUnit: number,
): SpinStrip {
  // The card is 2 units wide and 2·1.4 tall; the box adds room for the bevel's
  // highlight and the perspective's spread.
  const width = Math.ceil(pxPerUnit * 2.4);
  const height = Math.ceil(pxPerUnit * 3.2);
  const view = new CardView(plates, 1);
  // Into a texture, Y is flipped and so is the winding the shader culls by.
  view.facing(-1);
  const stage = new Container();
  stage.addChild(view);
  view.camera(width / 2, height / 2, pxPerUnit);

  const textures: Texture[] = [];
  for (let yaw = 0; yaw < 360; yaw += BAKE_STEP) {
    const spec: CardSpec = { face, yaw, pitch: REST_PITCH, finish, rankIndex, scale: 1, alpha: 1 };
    view.draw([spec], 0);
    const target = RenderTexture.create({ width, height, resolution: 1, antialias: true });
    renderer.render({ container: stage, target, clear: true });
    textures.push(target);
  }

  view.destroy();
  stage.destroy();
  return { textures, step: BAKE_STEP, pxPerUnit, face };
}

/** The frame of a strip nearest a yaw, wrapped into 0..360. */
export function frameAt(strip: SpinStrip, yaw: number): Texture {
  const wrapped = ((yaw % 360) + 360) % 360;
  const index = Math.round(wrapped / strip.step) % strip.textures.length;
  return strip.textures[index]!;
}

/** A sprite drawn from a strip, placed the way the shader would have placed the mesh. */
export function placeSprite(
  sprite: Sprite,
  strip: SpinStrip,
  spec: CardSpec,
  camera: { cx: number; cy: number; pxPerUnit: number; camDist: number },
): void {
  const z = spec.z ?? 0;
  const persp = camera.camDist / Math.max(0.001, camera.camDist - z);
  sprite.texture = frameAt(strip, spec.yaw);
  sprite.anchor.set(0.5);
  sprite.x = camera.cx + (spec.x ?? 0) * persp * camera.pxPerUnit;
  sprite.y = camera.cy - (spec.y ?? 0) * persp * camera.pxPerUnit;
  sprite.scale.set(((spec.scale ?? 1) * persp * camera.pxPerUnit) / strip.pxPerUnit);
  sprite.angle = spec.roll ?? 0;
  sprite.alpha = spec.alpha ?? 1;
  sprite.visible = sprite.alpha > 0.002;
}

export function destroyStrip(strip: SpinStrip): void {
  for (const texture of strip.textures) texture.destroy(true);
}
